import React, { useEffect, useMemo, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Typography,
} from '@crossnokaye/ux-common';

import { DialogAlert } from '@/components/dialog-alert';
import {
  getPlatformValidationRulesForChange,
  validateSettingChanges,
} from '@/helpers/settings-validation';
import { Stack } from '@/ux-common/components/stack/stack';

import { PreviewItem } from './preview-item';
import { PreviewSection } from './preview-section';
import type { SaveProgressStats } from './save-progress.utils';
import type { PreviewSaveStatus } from './setting-preview.types';
import type { SettingChangeGroup } from './settings-save.types';
import { SettingsSaveFooter } from './settings-save-footer';
import { SettingsSaveStatusMessage } from './settings-save-status-message';

export interface SettingsSaveDialogProps {
  groups: SettingChangeGroup<unknown>[];
  hasPendingSave: boolean;
  headerContent?: React.ReactNode;
  onCancelPending: () => void;
  onClose: () => void;
  onConfirm: () => void;
  onDiscard: (changeId: string) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  progressStats: SaveProgressStats;
  saveComplete: boolean;
  saveProgressByChangeId: Record<string, PreviewSaveStatus>;
}

export const SettingsSaveDialog = ({
  groups,
  hasPendingSave,
  headerContent,
  onCancelPending,
  onClose,
  onConfirm,
  onDiscard,
  onOpenChange,
  open,
  progressStats,
  saveComplete,
  saveProgressByChangeId,
}: SettingsSaveDialogProps): React.ReactNode => {
  const [isCancelled, setIsCancelled] = useState(false);

  const allChanges = useMemo(() => groups.flatMap((g) => g.changes), [groups]);

  // Run validation internally
  // TODO: Make validation pluggable via prop to support non-settings save flows.
  // Currently hardcodes getPlatformValidationRulesForChange — callers saving
  // non-settings data would need to inject their own rule resolver.
  const { issuesByChangeId, hasBlockingIssues } = useMemo(() => {
    const validationInputs = allChanges.map((change) => ({
      changeId: change.changeId,
      currentValue: change.currentValue,
      platformValidations: change.platformValidations,
    }));
    return validateSettingChanges(validationInputs, {
      getRulesForChange: getPlatformValidationRulesForChange,
    });
  }, [allChanges]);

  const changeCount = allChanges.length;
  const hasSaveProgress = Object.keys(saveProgressByChangeId).length > 0;

  useEffect(() => {
    if (!hasSaveProgress) setIsCancelled(false);
  }, [hasSaveProgress]);

  useEffect(() => {
    if (open && changeCount === 0 && !hasSaveProgress) {
      onClose();
    }
  }, [open, changeCount, onClose, hasSaveProgress]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} data-component="SettingsSaveDialog">
      <DialogContent className="text-foreground sm:max-w-215 bg-ckds-neutral-900 min-h-[30vh] max-h-[80vh] flex flex-col p-thin sm:p-stack-default">
        <DialogHeader className="shrink-0 items-start pt-stack-sm px-stack-sm sm:p-none">
          <DialogTitle>Review & Save Changes</DialogTitle>
        </DialogHeader>
        <div className="shrink-0 space-y-3 px-stack-sm sm:px-none">
          <SettingsSaveStatusMessage
            hasPendingSave={hasPendingSave}
            allSucceeded={progressStats.allSucceeded}
            saveComplete={saveComplete}
            failedCount={progressStats.failed}
            cancelledCount={progressStats.cancelled}
          />
          {headerContent}
        </div>

        <div className="flex-1 overflow-y-scroll min-h-0 px-stack-sm sm:px-none">
          {changeCount === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Typography variant="body" className="text-secondary">
                No unsaved changes
              </Typography>
            </div>
          ) : (
            <Stack className="stack-thin">
              {groups.map((group) => (
                <PreviewSection key={group.id} title={group.label}>
                  <Stack className="stack-thin">
                    {group.changes.map((change) => {
                      const rowSaveStatus = saveProgressByChangeId[change.changeId] ?? 'idle';
                      const rowIssues = issuesByChangeId[change.changeId] ?? [];
                      const warningIssue = rowIssues.find((issue) => issue.blocksSave);
                      const itemSaveStatus: PreviewSaveStatus =
                        warningIssue && rowSaveStatus === 'idle'
                          ? 'validation-warning'
                          : rowSaveStatus;
                      return (
                        <PreviewItem
                          key={change.changeId}
                          item={{
                            id: change.changeId,
                            label: change.label,
                            displayCurrent: change.displayCurrent,
                            displayOriginal: change.displayOriginal,
                            displayCurrentValue: change.displayCurrentValue,
                            displayCurrentUnits: change.displayCurrentUnits,
                          }}
                          message={change.message}
                          saveStatus={itemSaveStatus}
                          validationMessage={warningIssue?.message}
                          disableDiscard={hasPendingSave}
                          onDiscard={() => onDiscard(change.changeId)}
                        />
                      );
                    })}
                  </Stack>
                </PreviewSection>
              ))}
            </Stack>
          )}
        </div>
        {hasBlockingIssues && !hasPendingSave && (
          <div className="shrink-0 mx-stack-sm sm:mx-none">
            <DialogAlert variant="caution">
              One or more changes have validation errors. Please check the flagged values above.
            </DialogAlert>
          </div>
        )}
        <SettingsSaveFooter
          hasPendingSave={hasPendingSave}
          isCancelled={isCancelled}
          isSubmitting={hasPendingSave}
          saveComplete={saveComplete}
          allSucceeded={progressStats.allSucceeded}
          changeCount={changeCount}
          saveProgressByChangeId={saveProgressByChangeId}
          progressPercent={progressStats.progressPercent}
          disableSave={hasBlockingIssues}
          onCancelPending={() => {
            setIsCancelled(true);
            onCancelPending();
          }}
          onClose={onClose}
          onConfirm={() => {
            onConfirm();
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
